/**
 * MarkdownPanel — renders markdown explanation text with CSK brand styling.
 *
 * Uses react-markdown (a well-maintained, battle-tested library) instead of
 * a custom renderer. Applying CSK styling via the `components` prop keeps
 * the rendering simple and the bundle light.
 *
 * Why react-markdown vs. a custom renderer?
 *   - react-markdown handles edge cases (nested elements, escaping, code
 *     fences, links) that a hand-rolled renderer inevitably misses.
 *   - Security: react-markdown sanitizes HTML by default (no dangerouslySetInnerHTML).
 *   - The `components` override API lets us apply CSK brand tokens to every
 *     element without a separate CSS file or runtime CSS-in-JS library.
 *   - Bundle cost is well under 10 kB gzipped for the subset we use.
 *
 * Inline `$…$` and block `$$…$$` math expressions flow through
 * `remark-math` + `rehype-katex` so theorem statements render as proper
 * KaTeX glyphs without a separate component call. Consumers must import
 * `katex/dist/katex.min.css` once at the application entry — the demos
 * app does this from `src/styles/globals.css`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import { FONT, GRAY, PRIMARY } from "../design-system/tokens.js";
import { SafeLink } from "./SafeLink.js";

const REMARK_PLUGINS: React.ComponentProps<typeof ReactMarkdown>["remarkPlugins"] = [remarkMath];
const REHYPE_PLUGINS: React.ComponentProps<typeof ReactMarkdown>["rehypePlugins"] = [
  [
    rehypeKatex,
    {
      // `output: html` matches the standalone MathBlock / MathInline
      // surfaces. KaTeX's default output mixes HTML and MathML; the
      // duplicate tree confuses screen readers under react-markdown
      // because react-markdown also injects accessible labels.
      output: "html",
      // Keep the parser tolerant — a malformed expression should not
      // crash the surrounding paragraph.
      throwOnError: false,
      strict: "ignore",
      trust: false,
    },
  ],
];

export interface MarkdownPanelProps {
  /** Markdown string to render. */
  readonly children: string;
  /** Optional wrapping div style overrides. */
  readonly style?: React.CSSProperties;
}

/** CSK-styled react-markdown component overrides. */
// inline: local
const components: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  p: ({ children: c }) => (
    <p style={{ margin: "0.5rem 0", color: GRAY[700], lineHeight: "1.6" }}>{c}</p>
  ),
  strong: ({ children: c }) => <strong style={{ fontWeight: "600", color: GRAY[900] }}>{c}</strong>,
  em: ({ children: c }) => <em style={{ fontStyle: "italic", color: GRAY[700] }}>{c}</em>,
  code: ({ children: c }) => (
    <code
      style={{
        fontFamily: FONT.mono,
        fontSize: "0.85em",
        backgroundColor: GRAY[100],
        color: PRIMARY[700],
        padding: "0.1em 0.3em",
        borderRadius: "0.2rem",
      }}
    >
      {c}
    </code>
  ),
  ul: ({ children: c }) => (
    <ul style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: GRAY[700] }}>{c}</ul>
  ),
  ol: ({ children: c }) => (
    <ol style={{ margin: "0.5rem 0", paddingLeft: "1.5rem", color: GRAY[700] }}>{c}</ol>
  ),
  li: ({ children: c }) => <li style={{ margin: "0.25rem 0" }}>{c}</li>,
  blockquote: ({ children: c }) => (
    <blockquote
      style={{
        borderLeft: `3px solid ${PRIMARY[300]}`,
        paddingLeft: "0.75rem",
        margin: "0.5rem 0",
        color: GRAY[500],
        fontStyle: "italic",
      }}
    >
      {c}
    </blockquote>
  ),
  a: ({ href, children: c }) => (
    <SafeLink href={href ?? null} style={{ color: PRIMARY[700], textDecoration: "underline" }}>
      {c}
    </SafeLink>
  ),
  h1: ({ children: c }) => (
    <h1
      style={{
        fontSize: "1.25rem",
        fontWeight: "700",
        color: GRAY[900],
        margin: "1rem 0 0.5rem",
        fontFamily: FONT.heading,
      }}
    >
      {c}
    </h1>
  ),
  h2: ({ children: c }) => (
    <h2
      style={{
        fontSize: "1.1rem",
        fontWeight: "600",
        color: GRAY[900],
        margin: "0.75rem 0 0.25rem",
        fontFamily: FONT.heading,
      }}
    >
      {c}
    </h2>
  ),
  h3: ({ children: c }) => (
    <h3
      style={{
        fontSize: "1rem",
        fontWeight: "600",
        color: GRAY[700],
        margin: "0.5rem 0 0.25rem",
        fontFamily: FONT.heading,
      }}
    >
      {c}
    </h3>
  ),
};

/**
 * Renders a markdown string with CSK brand typography.
 *
 * Wraps react-markdown with a thin styling layer using CSK design tokens.
 * HTML in the markdown source is NOT rendered (react-markdown default).
 */
export const MarkdownPanel = React.memo(function MarkdownPanel({
  children,
  style,
}: MarkdownPanelProps): React.JSX.Element {
  return (
    <div
      style={{
        fontSize: "0.875rem",
        lineHeight: "1.6",
        fontFamily: FONT.body,
        ...style,
      }}
    >
      <ReactMarkdown
        components={components}
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
});
